import React, { useState, useEffect } from 'react';
import { useLocalization } from '../contexts/LocalizationContext';
import api, { getApiImageUrl } from '../services/api';
import './DragonLairScreen.css';

const DragonLairScreen = ({ currentPlayer }) => {
  const { t, language } = useLocalization();
  const [selectedItem, setSelectedItem] = useState(null);
  const [dragon, setDragon] = useState(null);
  const [collectibles, setCollectibles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch dragon lair data from backend
  const fetchDragonLairData = async () => {
    if (!currentPlayer) {
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      const response = await api.get(`/api/dragon-lair`, {
        params: { lang: language, player_id: currentPlayer.id }
      });
      
      if (response.status === 200) {
        const data = response.data;
        
        // Update dragon data
        const hasDragon = data.dragon.dragon_name != null;
        const dragonData = {
          name: data.dragon.dragon_name ? data.dragon.dragon_name.replace('_', ' ').replace('-', ' ').toUpperCase() : 'DRAGON',
          level: data.dragon.dragon_level,
          speed: data.dragon.speed,
          power: data.dragon.power,
          wisdom: data.dragon.wisdom,
          defense: data.dragon.defense,
          treasureCount: data.dragon.treasure_count,
          stage: data.dragon.dragon_stage,
          evolution: data.dragon.current_evolution,
          hasSelected: hasDragon
        };
        setDragon(dragonData);
        setCollectibles(data.collectibles);
      } else {
        setError(`Failed to load dragon data: ${response.status}`);
      }
    } catch (error) {
      console.error('Error fetching dragon lair data:', error);
      setError('Failed to connect to server. Please try again.');
    } finally {
      setLoading(false);
    }
  };




  useEffect(() => {
    if (currentPlayer) {
      fetchDragonLairData();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language, currentPlayer]);
  


  const getTierColor = (tier) => {
    const colors = {
      1: '#8B7355', // Bronze
      2: '#C0C0C0', // Silver  
      3: '#FFD700', // Gold
      4: '#9B59B6'  // Purple
    };
    return colors[tier] || '#8B7355';
  };


  const openModal = (item) => {
    setSelectedItem(item);
  };

  const closeModal = () => {
    setSelectedItem(null);
  };

  // Show loading state
  if (loading) {
    return <div className="loading">{t('common.loading')}</div>;
  }
  
  // Show error state
  if (error) {
    return (
      <div className="error-container">
        <div className="error-message">
          <h3>{t('dragonLair.errorLoading')}</h3>
          <p>{error}</p>
          <button onClick={() => {
            setError(null);
            fetchDragonLairData();
          }}>
            {t('dragon.tryAgain')}
          </button>
        </div>
      </div>
    );
  }
  
  // Show fallback if no dragon data
  if (!dragon) {
    return <div className="loading">{t('common.loading')}</div>;
  }

  return (
    <div className="dragon-lair-screen">
      {/* Background and Dragon Section */}
      <div className="lair-background">
        <div 
          className="background-cave"
          style={{ 
            backgroundImage: currentPlayer ? 
              `url('${getApiImageUrl(`/api/dragon-lair/composite-image?player_id=${currentPlayer.id}`)}')` : 
              'none'
          }}
        >
          {/* Composite image includes both background and dragon positioned correctly */}
        </div>
      </div>

      {/* Dragon Stats Section */}
      <div className="dragon-stats">
        {/* Dragon Type - only show when dragon is selected */}
        {dragon?.hasSelected && <h2 className="dragon-name">{dragon.name}</h2>}
        
        {/* Level with icon - only show when dragon is selected */}
        {dragon?.hasSelected && (
          <div className="level-section">
            <img src={getApiImageUrl('/api/artwork/level.png')} alt="Level" className="level-icon" />
            <span className="level-text">{t('dragonLair.level')} {dragon.level}</span>
          </div>
        )}
        
        {/* Stats Grid - 4 stats in order: speed, power, defense, wisdom */}
        <div className="stats-row">
          <div className="stat-column">
            <img src={getApiImageUrl('/api/artwork/speed.png')} alt="Speed" className="stat-icon" />
            <span className="stat-value">{dragon.speed}</span>
            <span className="stat-name">{t('dragonLair.speed')}</span>
          </div>
          <div className="stat-column">
            <img src={getApiImageUrl('/api/artwork/power.png')} alt="Power" className="stat-icon" />
            <span className="stat-value">{dragon.power}</span>
            <span className="stat-name">{t('dragonLair.power')}</span>
          </div>
          <div className="stat-column">
            <img src={getApiImageUrl('/api/artwork/defense.png')} alt="Defense" className="stat-icon" />
            <span className="stat-value">{dragon.defense}</span>
            <span className="stat-name">{t('dragonLair.defense')}</span>
          </div>
          <div className="stat-column">
            <img src={getApiImageUrl('/api/artwork/wisdom.png')} alt="Wisdom" className="stat-icon" />
            <span className="stat-value">{dragon.wisdom}</span>
            <span className="stat-name">{t('dragonLair.wisdom')}</span>
          </div>
        </div>
        
        {/* Treasures at bottom */}
        <div className="treasure-section">
          <img src={getApiImageUrl('/api/artwork/treasure.png')} alt="Treasure" className="treasure-icon" />
          <span className="treasure-count">{dragon.treasureCount}</span>
        </div>
      </div>

      {/* Collectibles Grid */}
      <div className="collectibles-section">
        <h3 className="section-title">{t('dragonLair.yourCollection')}</h3>
        <div className="collectibles-grid">
          {collectibles.map((item, index) => (
            <div 
              key={`${item.id}-${index}`}
              className="collectible-item"
              onClick={() => openModal(item)}
              style={{ borderColor: getTierColor(item.tier) }}
            >
              <div className="collectible-image">
                {item.image ? (
                  <img src={getApiImageUrl(item.image)} alt={item.name} />
                ) : (
                  <div className="image-placeholder">🏆</div>
                )}
              </div>
              <div className="collectible-name">{item.name}</div>
              <div className="collectible-stats">
                <span className="stat">{t('dragonLair.speed').charAt(0).toUpperCase()}:{item.speed}</span>
                <span className="stat">{t('dragonLair.power').charAt(0).toUpperCase()}:{item.power}</span>
                <span className="stat">{t('dragonLair.defense').charAt(0).toUpperCase()}:{item.defense}</span>
                <span className="stat">{t('dragonLair.wisdom').charAt(0).toUpperCase()}:{item.wisdom}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Collectible Details Modal */}
      {selectedItem && (
        <div className="dragon-modal-overlay" onClick={closeModal}>
          <div className="dragon-modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="dragon-modal-close" onClick={closeModal}>×</button>
            <div className="dragon-modal-header">
              <div className="dragon-modal-title">
                <h3>{selectedItem.name}</h3>
              </div>
              <div className="dragon-modal-image">
                {selectedItem.image ? (
                  <img 
                    src={getApiImageUrl(selectedItem.image)} 
                    alt={selectedItem.name}
                  />
                ) : (
                  <div className="image-placeholder">🏆</div>
                )}
              </div>
            </div>
            <div className="dragon-modal-stats">
              <div className="stat-row">
                <span>{t('dragonLair.speed')}: {selectedItem.speed}</span>
                <span>{t('dragonLair.power')}: {selectedItem.power}</span>
              </div>
              <div className="stat-row">
                <span>{t('dragonLair.wisdom')}: {selectedItem.wisdom}</span>
                <span>{t('dragonLair.defense')}: {selectedItem.defense}</span>
              </div>
            </div>
            <div className="dragon-modal-story">
              <h4>{t('dragonLair.story')}</h4>
              <p>{selectedItem.story}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DragonLairScreen;